import React, { useEffect, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { FloatingToolbar } from './FloatingToolbar';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { EDITOR_PURIFY_CONFIG } from '@/lib/constants';

interface DocEditorProps {
  html: string;
  onChange: (newHtml: string) => void;
}

export function DocEditor({ html, onChange }: DocEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastHtmlRef = useRef<string>('');
  const isInternalChangeRef = useRef<boolean>(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const [toolbarPos, setToolbarPos] = useState<{ x: number; y: number } | null>(null);
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const [improveStatus, setImproveStatus] = useState<'idle' | 'loading' | 'done'>('idle');

  const initNode = useCallback((node: Node) => {
    if (node instanceof HTMLElement) {
      const tags = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'LI', 'TD', 'TH'];
      if (tags.includes(node.tagName)) {
        if (!node.classList.contains('docforge-editable')) {
          node.contentEditable = 'true';
          node.classList.add('docforge-editable');
        }
        if (!node.dataset.original) {
          node.dataset.original = node.innerHTML;
        }
      }
      node.childNodes.forEach(child => initNode(child));
    }
  }, []);

  const setupObserver = useCallback(() => {
    if (!containerRef.current) return;
    observerRef.current = new MutationObserver((mutations) => {
      let needsSync = false;
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              initNode(node);
            }
          });
          needsSync = true;
        }
        if (mutation.type === 'characterData' || mutation.type === 'attributes') {
          needsSync = true;
        }
      });
      if (needsSync && containerRef.current) {
        const currentHtml = containerRef.current.innerHTML;
        if (currentHtml !== lastHtmlRef.current) {
          lastHtmlRef.current = currentHtml;
          isInternalChangeRef.current = true;
          onChange(currentHtml);
        }
      }
    });
    observerRef.current.observe(containerRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }, [onChange, initNode]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (html !== lastHtmlRef.current && !isInternalChangeRef.current) {
      if (observerRef.current) observerRef.current.disconnect();
      const sanitized = DOMPurify.sanitize(html, EDITOR_PURIFY_CONFIG);
      containerRef.current.innerHTML = sanitized;
      lastHtmlRef.current = sanitized;
      initNode(containerRef.current);
      setupObserver();
    }
    isInternalChangeRef.current = false;
  }, [html, initNode, setupObserver]);

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const target = e.target as HTMLElement;
    const editable = target.closest('.docforge-editable') as HTMLElement;
    
    if (editable && containerRef.current.contains(editable)) {
      const rect = editable.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      
      const toolbarWidth = 240;
      const toolbarHeight = 44;
      
      let x = (rect.left + rect.width / 2) - containerRect.left - toolbarWidth / 2;
      x = Math.max(10, Math.min(x, containerRect.width - toolbarWidth - 10));
      
      let y = rect.top - containerRect.top - toolbarHeight - 12;
      if (y < 0) {
        y = rect.bottom - containerRect.top + 12;
      }
      
      setToolbarPos({ x, y });
      setActiveElement(editable);
    } else {
      const toolbarElement = document.getElementById('floating-toolbar');
      if (toolbarElement && !toolbarElement.contains(target)) {
        setToolbarPos(null);
        setActiveElement(null);
      }
    }
  }, []);

  const handleImprove = async () => {
    if (!activeElement || !containerRef.current?.contains(activeElement)) return;
    setImproveStatus('loading');
    const currentText = activeElement.innerText;
    const currentTag = activeElement.tagName.toLowerCase();
    try {
      const res = await fetch('/api/improve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: currentText,
          tag: currentTag
        })
      });
      const result = await res.json();
      if (result.success && activeElement && containerRef.current?.contains(activeElement)) {
        const originalContent = activeElement.dataset.original;
        if (result.data.includes('<') && result.data.includes('>')) {
          activeElement.innerHTML = result.data;
        } else {
          activeElement.innerText = result.data;
        }
        if (originalContent) activeElement.dataset.original = originalContent;
        setImproveStatus('done');
        setTimeout(() => setImproveStatus('idle'), 2000);
      } else {
        setImproveStatus('idle');
      }
    } catch (e) {
      setImproveStatus('idle');
      toast.error('The forge cooled unexpectedly.');
    }
  };

  const handleReset = () => {
    if (activeElement && activeElement.dataset.original) {
      activeElement.innerHTML = activeElement.dataset.original;
      toast.info('Restored original prose.');
    }
  };

  const handleEdit = () => {
    if (activeElement) {
      activeElement.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(activeElement);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };

  return (
    <div 
      className="relative w-full h-full p-8 md:p-20 scroll-smooth"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => {
        setTimeout(() => {
          const hovered = document.querySelectorAll(':hover');
          const isOverToolbar = Array.from(hovered).some(el => el.id === 'floating-toolbar');
          if (!isOverToolbar) {
            setToolbarPos(null);
            setActiveElement(null);
          }
        }, 300);
      }}
    >
      <div
        ref={containerRef}
        className="doc-content outline-none selection:bg-primary/20"
        spellCheck="false"
        onBlur={() => {
          if (containerRef.current) {
             const currentHtml = containerRef.current.innerHTML;
             if (currentHtml !== lastHtmlRef.current) {
               lastHtmlRef.current = currentHtml;
               onChange(currentHtml);
             }
          }
        }}
      />
      <FloatingToolbar
        position={toolbarPos}
        onImprove={handleImprove}
        onReset={handleReset}
        onEdit={handleEdit}
        status={improveStatus}
      />
    </div>
  );
}